import { call, put, takeLatest } from 'redux-saga/effects';
import SuperFetch from 'library/helpers/superFetch';
import { Notification } from 'zComponents/atoms';
import { featureFlagActions } from './slice';

const errMsg = (res, fallback) => (res && res.error && (res.error.message || res.error)) || fallback;

// GET /v5/:orgID/feature-flags?developer=1 → { flags: {code:bool}, developer: bool }.
export function* getFeatureFlags() {
	try {
		// developer=1 is a query param, so it lands in the 3rd arg — SuperFetch's 2nd arg is the
		// (GET-ignored) body slot, so passing it there would drop it and the panel would never unlock.
		const res = yield call(() => SuperFetch.get('/feature-flags', {}, { developer: 1 }));
		// 200 normally; 304 defensively (SuperFetch sends cache:no-store, so 304 shouldn't
		// occur — but a 304 must not leave flags permanently unloaded).
		if (res.statusCode === 200 || res.statusCode === 304) {
			yield put(featureFlagActions.loadFeatureFlagsSuccess(res.data || {}));
		} else {
			throw Error(errMsg(res, 'Failed to load feature flags.'));
		}
	} catch (error) {
		yield put(featureFlagActions.loadFeatureFlagsError(error.message || String(error)));
	}
}

// ── Developer admin (developerCheck-gated on the server) ──

// GET /feature-flags/admin → { flags: [...], overrides: [...] }.
export function* getRegistry() {
	try {
		const res = yield call(() => SuperFetch.get('/feature-flags/admin'));
		if (res.statusCode === 200) {
			yield put(featureFlagActions.loadRegistrySuccess(res.data || {}));
		} else {
			throw Error(errMsg(res, 'Failed to load the feature-flag registry.'));
		}
	} catch (error) {
		yield put(featureFlagActions.loadRegistryError(error.message || String(error)));
	}
}

// GET /organisations/AllOrgs → [{ orgID, name, ... }]. Powers the override modal's org lookup so
// a dev picks the target org by name instead of typing a raw ID. Silent on failure — the modal
// falls back to a plain numeric entry, so a lookup outage never blocks setting an override.
export function* getOrgs() {
	try {
		const res = yield call(() => SuperFetch.get('/organisations/AllOrgs'));
		if (res.statusCode === 200) {
			yield put(featureFlagActions.loadOrgsSuccess(res.data || []));
		} else {
			yield put(featureFlagActions.loadOrgsError());
		}
	} catch (error) {
		yield put(featureFlagActions.loadOrgsError());
	}
}

// Shared write runner: perform, notify, reload the registry + resolved flags on success.
function* runMutation(fn, successMsg, okStatuses = [200, 201]) {
	try {
		const res = yield call(fn);
		if (okStatuses.includes(res.statusCode)) {
			Notification('success', successMsg);
			yield put(featureFlagActions.loadRegistry());
			yield put(featureFlagActions.loadFeatureFlags()); // refresh the caller's own resolved map
		} else {
			Notification('error', errMsg(res, 'Feature-flag update failed.'));
		}
	} catch (error) {
		Notification('error', error.message || 'Feature-flag update failed.');
	} finally {
		yield put(featureFlagActions.mutateComplete());
	}
}

export function* setPlatformDefault({ payload }) {
	const { code, enabled } = payload;
	yield* runMutation(
		() => SuperFetch.put(`/feature-flags/admin/platform/${code}`, { enabled }),
		`Platform default ${enabled ? 'enabled' : 'disabled'} for ${code}.`
	);
}

export function* setOverride({ payload }) {
	const { code, targetOrgID, state, reason } = payload;
	yield* runMutation(
		() => SuperFetch.put(`/feature-flags/admin/override/${targetOrgID}/${code}`, { state, reason }),
		`Override for ${code} @ org ${targetOrgID} set to ${state}.`
	);
}

export function* registerFlag({ payload }) {
	yield* runMutation(() => SuperFetch.post('/feature-flags/admin', payload), `Flag ${payload.code} registered.`);
}

export function* updateFlagMeta({ payload }) {
	const { code, owner, removeBy } = payload;
	yield* runMutation(
		() => SuperFetch.put(`/feature-flags/admin/${code}`, { owner, removeBy }),
		`Flag ${code} updated.`
	);
}

export function* archiveFlag({ payload }) {
	const { code } = payload;
	yield* runMutation(() => SuperFetch.delete(`/feature-flags/admin/${code}`), `Flag ${code} archived.`);
}

export function* unarchiveFlag({ payload }) {
	const { code } = payload;
	yield* runMutation(() => SuperFetch.post(`/feature-flags/admin/${code}/unarchive`, {}), `Flag ${code} re-enabled.`);
}

export default function* featureFlagsSaga() {
	yield takeLatest(featureFlagActions.loadFeatureFlags, getFeatureFlags);
	yield takeLatest(featureFlagActions.loadRegistry, getRegistry);
	yield takeLatest(featureFlagActions.loadOrgs, getOrgs);
	yield takeLatest(featureFlagActions.setPlatformDefault, setPlatformDefault);
	yield takeLatest(featureFlagActions.setOverride, setOverride);
	yield takeLatest(featureFlagActions.registerFlag, registerFlag);
	yield takeLatest(featureFlagActions.updateFlagMeta, updateFlagMeta);
	yield takeLatest(featureFlagActions.archiveFlag, archiveFlag);
	yield takeLatest(featureFlagActions.unarchiveFlag, unarchiveFlag);
}
